import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ScrollView, Icon3 } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedJobAtom,
    selectedVersionAtom,
    selectedVersionLockedAtom,
    jobUserDataAtom,
    ohPercentAtom,
    userNameAtom,
    userIdAtom,
    coValidationChipsAtom,
} from '../store/company';
import { updateUserDataArray, pNum, fNum, deleteCO, deleteUserData } from '../db/company';
import { NumberInput } from './numberInput';

/**
 * COLogEntryClassic - colog.js-style horizontal table UI that saves as
 * "Change Order" + "Change Order Header" tabs (same as cologEntry.js / cologUpload.js).
 *
 * Each row is a single Change Order. Inline NumberInput fields for every cost code.
 * Expandable column sections: labor, equipment, materials, subs, other, units.
 * Debounced save per-field that writes the correct multi-row DB format.
 */

// Keep in sync with MAX_DESCRIPTION_LENGTH in cologEntry.js
const MAX_DESCRIPTION_LENGTH = 120;

const COLogEntryClassic = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [ohPercent] = useAtom(ohPercentAtom);
    const [userName] = useAtom(userNameAtom);
    const [userId] = useAtom(userIdAtom);

    const [coLogs, setCOLogs] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    // Expand/collapse state
    const [totalExpanded, setTotalExpanded] = useState(false);
    const [laborExpanded, setLaborExpanded] = useState(false);
    const [equipmentExpanded, setEquipmentExpanded] = useState(false);
    const [materialsExpanded, setMaterialsExpanded] = useState(false);
    const [subsExpanded, setSubsExpanded] = useState(false);
    const [otherExpanded, setOtherExpanded] = useState(false);
    const [unitsExpanded, setUnitsExpanded] = useState(false);
    const [psrExpanded, setPsrExpanded] = useState(false);

    const focusedId = useRef(null);
    const autoSaveTimers = useRef({});

    // Footer totals
    const [totals, setTotals] = useState({});
    const [totalsApproved, setTotalsApproved] = useState({});
    const [totalsPending, setTotalsPending] = useState({});

    const statusOptions = [
        { label: 'Approved', value: 'Approved' },
        { label: 'Pending - Performed', value: 'Pending - Performed' },
        { label: 'Pending - Not Performed', value: 'Pending - Not Performed' },
        { label: 'Not Submitted', value: 'Not Submitted' },
        { label: 'Denied', value: 'Denied' },
        { label: 'Passthru', value: 'Passthru' },
    ];

    const generateId = () => Math.floor(10000000 + Math.random() * 90000000).toString();

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return '';
            date.setHours(date.getHours() + 6);
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
        } catch { return ''; }
    };

    // -----------------------------------------------------------
    // Style helper matching colog.js
    // -----------------------------------------------------------
    const s = (w) => StyleSheet.create({
        container: {
            flexDirection: 'row',
            paddingBottom: 0,
        },
        spanT: {
            width: w ? w : 100,
            alignItems: 'center',
            fontSize: 13,
            padding: 10,
            textAlign: 'right',
            justifyContent: 'flex-end',
            borderBottomWidth: 1,
            borderBottomColor: '#e2e8f0',
            zIndex: 1,
        },
        spanH: {
            width: w ? w : 100,
            alignItems: 'center',
            fontSize: 13,
            fontWeight: '600',
            padding: 12,
            textAlign: 'right',
            borderBottomWidth: 2,
            borderBottomColor: '#2563eb',
            backgroundColor: '#f8fafc',
            color: '#1e293b',
        },
        spanHI: {
            width: w ? w : 100,
            alignItems: 'center',
            fontSize: 16,
            padding: 5,
            flexDirection: 'row',
            justifyContent: 'space-between',
            display: 'flex',
        },
        span1T: {
            width: w ? w : 100,
            maxWidth: w ? w : 100,
            alignItems: 'center',
            fontSize: 16,
            padding: 5,
            textAlign: 'right',
            borderColor: '#aeadaf',
            borderWidth: 1,
            backgroundColor: '#efefef',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
        },
    });

    // -----------------------------------------------------------
    // Load data from jobUserData
    // -----------------------------------------------------------
    useEffect(() => {
        if (jobUserData && selectedJob && selectedVersion) {
            loadEntries();
        }
    }, [jobUserData, selectedJob, selectedVersion]);

    const loadEntries = () => {
        try {
            const headerData = jobUserData.filter(row =>
                row.Forecast_Tab === "Change Order Header" &&
                row.BU_ID == selectedJob &&
                row.Version_GUID == selectedVersion.toString()
            );
            const costData = jobUserData.filter(row =>
                row.Forecast_Tab === "Change Order" &&
                row.BU_ID == selectedJob &&
                row.Version_GUID == selectedVersion.toString()
            );

            // Helper: get parent CO number (1.1, 1.2 → "1"; "2" → "2")
            const parentCo = (coNo) => {
                const n = parseFloat(coNo);
                return isNaN(n) ? coNo : Math.floor(n).toString();
            };

            // Group headers by PARENT CO (merge sub-item headers into one)
            const headersByParent = {};
            headerData.forEach(h => {
                const coNo = h.rowLabel || h.Row_Label || '';
                if (!coNo) return;
                const pco = parentCo(coNo);
                if (!headersByParent[pco]) {
                    headersByParent[pco] = { ...h };
                } else {
                    const existing = headersByParent[pco];
                    const extVal = pNum(h.valueAmount ?? h.Value_Amount ?? 0);
                    existing.valueAmount = pNum(existing.valueAmount ?? existing.Value_Amount ?? 0) + extVal;
                    if (!existing.description && !existing.Description) {
                        existing.description = h.description || h.Description;
                    }
                    if (!existing.notes && !existing.Notes) {
                        existing.notes = h.notes || h.Notes;
                    }
                }
            });

            // Group cost rows by ORIGINAL CO number (no merging)
            const costsByCoNo = {};
            costData.forEach(c => {
                const coNo = c.rowLabel || c.Row_Label || '';
                if (!coNo) return;
                if (!costsByCoNo[coNo]) costsByCoNo[coNo] = [];
                costsByCoNo[coNo].push(c);
            });

            const allCoNos = new Set([...Object.keys(headersByParent), ...Object.keys(costsByCoNo)]);
            const entries = [];

            allCoNos.forEach(coNo => {
                const pco = parentCo(coNo);
                const header = headersByParent[pco]; // look up merged parent header
                const costs = costsByCoNo[coNo] || [];

                // Build flat field map – use += to accumulate sub-items
                // Handle combined rows: a single DB row with both hours AND dollars
                const fieldMap = {};
                const acc = (key, v) => { fieldMap[key] = (fieldMap[key] || 0) + pNum(v); };

                costs.forEach(c => {
                    const fn = c.forecastFieldName || c.Forecast_Field_Name || '';
                    const ct = (c.costType || c.Cost_Type || '').toString();
                    const val = pNum(c.valueAmount ?? c.Value_Amount ?? 0);
                    const hrs = pNum(c.hours ?? c.Hours ?? 0);
                    const qty = pNum(c.quantity ?? c.Quantity ?? 0);

                    if (fn === 'unitPrice' || fn === 'tAndM' || fn === 'changeOrders' || fn === 'reductionOfScope') {
                        acc(fn, val);
                    } else if (fn === 'totalLaborHours') {
                        acc('totalLaborHours', hrs || val);
                        // Combined row: also has dollar amount → capture as cost code
                        if (val && hrs) acc('51100', val);
                    } else if (fn === 'totalEquipmentHours') {
                        acc('totalEquipmentHours', hrs || val);
                        if (val && hrs) acc('52100', val);
                    } else if (fn === 'budgetQty' || fn === 'Quantity') {
                        acc('budgetQty', qty || val);
                    } else if (ct && ct.match(/^5\d{4}$/)) {
                        acc(ct, val);
                        // Combined row: cost-code row that also carries hours
                        if (hrs && (ct === '51100')) acc('totalLaborHours', hrs);
                        else if (hrs && (ct === '52100')) acc('totalEquipmentHours', hrs);
                        if (qty) acc('budgetQty', qty);
                    }
                });

                const mapType = {
                    "Internal C": "Internal Change",
                    "Customer R": "Customer Requested",
                    "Unit Adder": "Unit Adder",
                    "Bid Miss": "Bid Miss",
                    "Internal Change": "Internal Change",
                    "Customer Requested": "Customer Requested",
                };

                entries.push({
                    _localId: generateId(),
                    coNo: coNo,
                    phase: header?.costCode || header?.Cost_Code || '',
                    description: header?.description || header?.Description || '',
                    status: header?.status || header?.Status || costs[0]?.status || costs[0]?.Status || '',
                    type: mapType[header?.PO_Number] || header?.PO_Number || '',
                    revenueType: header?.resourceCode || header?.Resource_Code || '',
                    notes: header?.notes || header?.Notes || '',
                    initiated: header?.projectStart || header?.Project_Start || '',
                    submitted: header?.projectEnd || header?.Project_End || '',
                    signed: header?.phaseStart || header?.Phase_Start || '',
                    extension: header?.valueAmount || header?.Value_Amount || '',
                    task: header?.active || header?.Active || false,
                    criticalPath: header?.equipmentTier || header?.Equipment_Tier || false,
                    // Revenue fields
                    unitPrice: fieldMap['unitPrice'] || 0,
                    tAndM: fieldMap['tAndM'] || 0,
                    changeOrders: fieldMap['changeOrders'] || 0,
                    reductionOfScope: fieldMap['reductionOfScope'] || 0,
                    // Units
                    budgetQty: fieldMap['budgetQty'] || 0,
                    totalLaborHours: fieldMap['totalLaborHours'] || 0,
                    totalEquipmentHours: fieldMap['totalEquipmentHours'] || 0,
                    // Cost fields
                    '51100': fieldMap['51100'] || 0,
                    '51200': fieldMap['51200'] || 0,
                    '51300': fieldMap['51300'] || 0,
                    '51400': fieldMap['51400'] || 0,
                    '51500': fieldMap['51500'] || 0,
                    '51600': fieldMap['51600'] || 0,
                    '51700': fieldMap['51700'] || 0,
                    '51800': fieldMap['51800'] || 0,
                    '52100': fieldMap['52100'] || 0,
                    '52200': fieldMap['52200'] || 0,
                    '52300': fieldMap['52300'] || 0,
                    '52400': fieldMap['52400'] || 0,
                    '53100': fieldMap['53100'] || 0,
                    '53200': fieldMap['53200'] || 0,
                    '53300': fieldMap['53300'] || 0,
                    '53400': fieldMap['53400'] || 0,
                    '54100': fieldMap['54100'] || 0,
                    '54200': fieldMap['54200'] || 0,
                    '54300': fieldMap['54300'] || 0,
                    '55100': fieldMap['55100'] || 0,
                    '55200': fieldMap['55200'] || 0,
                    '55300': fieldMap['55300'] || 0,
                    '55400': fieldMap['55400'] || 0,
                    '55900': fieldMap['55900'] || 0,
                    '56100': fieldMap['56100'] || 0,
                    '56200': fieldMap['56200'] || 0,
                    '56300': fieldMap['56300'] || 0,
                    '56400': fieldMap['56400'] || 0,
                    '56500': fieldMap['56500'] || 0,
                    '56600': fieldMap['56600'] || 0,
                    '56700': fieldMap['56700'] || 0,
                    '56800': fieldMap['56800'] || 0,
                    '57100': fieldMap['57100'] || 0,
                    '57200': fieldMap['57200'] || 0,
                    '57300': fieldMap['57300'] || 0,
                    '57900': fieldMap['57900'] || 0,
                    _isNew: false,
                    _dirty: false,
                });
            });

            entries.sort((a, b) => {
                const aNum = parseFloat(a.coNo) || 0;
                const bNum = parseFloat(b.coNo) || 0;
                return aNum - bNum;
            });

            setCOLogs(entries);
        } catch (error) {
            console.error('Error loading classic CO entries:', error);
        }
    };

    // -----------------------------------------------------------
    // Computed values helper
    // -----------------------------------------------------------
    const calcRow = (row) => {
        const totalRevenue = pNum(row.unitPrice) + pNum(row.tAndM) + pNum(row.changeOrders) + pNum(row.reductionOfScope);
        const labor = pNum(row['51100']) + pNum(row['51200']) + pNum(row['51300']) + pNum(row['51400']) + pNum(row['51500']) + pNum(row['51600']) + pNum(row['51700']) + pNum(row['51800']);
        const equipment = pNum(row['52100']) + pNum(row['52200']) + pNum(row['52300']) + pNum(row['52400']);
        const materialsTotal = pNum(row['53100']) + pNum(row['53200']) + pNum(row['53300']) + pNum(row['53400']);
        const subs = pNum(row['54100']) + pNum(row['54200']) + pNum(row['54300']);
        const otherExpenses = pNum(row['55100']) + pNum(row['55200']) + pNum(row['55300']) + pNum(row['55400']) + pNum(row['55900'])
            + pNum(row['56100']) + pNum(row['56200']) + pNum(row['56300']) + pNum(row['56400']) + pNum(row['56500'])
            + pNum(row['56600']) + pNum(row['56700']) + pNum(row['56800'])
            + pNum(row['57100']) + pNum(row['57200']) + pNum(row['57300']) + pNum(row['57900']);
        const overhead = totalRevenue * pNum(ohPercent) / 100;
        const totalCosts = labor + equipment + materialsTotal + subs + otherExpenses + overhead;
        const grossProfit = totalRevenue - totalCosts;
        const gpPercent = totalRevenue !== 0 ? (grossProfit / totalRevenue * 100) : 0;
        return { totalRevenue, labor, equipment, materialsTotal, subs, otherExpenses, overhead, totalCosts, grossProfit, gpPercent };
    };

    // -----------------------------------------------------------
    // Recalculate footer totals
    // -----------------------------------------------------------
    useEffect(() => {
        const t = {};
        const tApproved = {};
        const tPending = {};
        const sumKeys = [
            'unitPrice', 'tAndM', 'changeOrders', 'reductionOfScope',
            'budgetQty', 'totalLaborHours', 'totalEquipmentHours',
            '51100', '51200', '51300', '51400', '51500', '51600', '51700', '51800',
            '52100', '52200', '52300', '52400',
            '53100', '53200', '53300', '53400',
            '54100', '54200', '54300',
            '55100', '55200', '55300', '55400', '55900',
            '56100', '56200', '56300', '56400', '56500', '56600', '56700', '56800',
            '57100', '57200', '57300', '57900',
        ];
        sumKeys.forEach(k => { t[k] = 0; tApproved[k] = 0; tPending[k] = 0; });
        let tRevenue = 0, tCosts = 0, tGP = 0;
        let aRevenue = 0, aCosts = 0, aGP = 0;
        let pRevenue = 0, pCosts = 0, pGP = 0;

        const approvedStatuses = ['Approved'];
        const pendingStatuses = ['Pending - Performed', 'Pending - Not Performed', 'Not Submitted', 'Denied', 'Passthru'];

        coLogs.forEach(row => {
            const c = calcRow(row);
            const isApproved = approvedStatuses.includes(row.status);
            const isPending = !isApproved;

            sumKeys.forEach(k => {
                t[k] += pNum(row[k]);
                if (isApproved) tApproved[k] += pNum(row[k]);
                if (isPending) tPending[k] += pNum(row[k]);
            });

            tRevenue += c.totalRevenue; tCosts += c.totalCosts; tGP += c.grossProfit;
            if (isApproved) { aRevenue += c.totalRevenue; aCosts += c.totalCosts; aGP += c.grossProfit; }
            if (isPending) { pRevenue += c.totalRevenue; pCosts += c.totalCosts; pGP += c.grossProfit; }
        });

        const deriveTotals = (obj, rev, costs, gp) => {
            obj.totalRevenue = rev;
            obj.totalCosts = costs;
            obj.grossProfit = gp;
            obj.gpPercent = rev !== 0 ? (gp / rev * 100) : 0;
            obj.labor = obj['51100'] + obj['51200'] + obj['51300'] + obj['51400'] + obj['51500'] + obj['51600'] + obj['51700'] + obj['51800'];
            obj.equipment = obj['52100'] + obj['52200'] + obj['52300'] + obj['52400'];
            obj.materialsTotal = obj['53100'] + obj['53200'] + obj['53300'] + obj['53400'];
            obj.subs = obj['54100'] + obj['54200'] + obj['54300'];
            obj.otherExpenses = obj['55100'] + obj['55200'] + obj['55300'] + obj['55400'] + obj['55900']
                + obj['56100'] + obj['56200'] + obj['56300'] + obj['56400'] + obj['56500']
                + obj['56600'] + obj['56700'] + obj['56800']
                + obj['57100'] + obj['57200'] + obj['57300'] + obj['57900'];
            obj.overhead = rev * pNum(ohPercent) / 100;
        };

        deriveTotals(t, tRevenue, tCosts, tGP);
        deriveTotals(tApproved, aRevenue, aCosts, aGP);
        deriveTotals(tPending, pRevenue, pCosts, pGP);

        setTotals(t);
        setTotalsApproved(tApproved);
        setTotalsPending(tPending);
    }, [coLogs, ohPercent]);

    // -----------------------------------------------------------
    // Validation: compare displayed totals vs raw DB data
    // -----------------------------------------------------------
    const [, setValidationChips] = useAtom(coValidationChipsAtom);

    useEffect(() => {
        if (!jobUserData || !selectedJob || !selectedVersion) { setValidationChips([]); return; }
        try {
            const costData = jobUserData.filter(row =>
                row.Forecast_Tab === "Change Order" &&
                row.BU_ID == selectedJob &&
                row.Version_GUID == selectedVersion.toString()
            );

            // Compute raw totals directly from DB rows
            const raw = { revenue: 0, labor: 0, equipment: 0, materials: 0, subs: 0, other: 0, laborHrs: 0, equipHrs: 0, budQty: 0 };
            costData.forEach(c => {
                const fn = c.forecastFieldName || c.Forecast_Field_Name || '';
                const ct = (c.costType || c.Cost_Type || '').toString();
                const val = pNum(c.valueAmount ?? c.Value_Amount ?? 0);
                const hrs = pNum(c.hours ?? c.Hours ?? 0);
                const qty = pNum(c.quantity ?? c.Quantity ?? 0);

                // Revenue
                if (fn === 'unitPrice' || fn === 'tAndM' || fn === 'changeOrders' || fn === 'reductionOfScope') {
                    raw.revenue += val;
                }
                // Hours/Qty
                if (fn === 'totalLaborHours') raw.laborHrs += (hrs || val);
                if (fn === 'totalEquipmentHours') raw.equipHrs += (hrs || val);
                if (fn === 'budgetQty') raw.budQty += (qty || val);
                // Cost categories
                if (ct.match(/^51\d{3}$/) && fn !== 'totalLaborHours') raw.labor += val;
                else if (ct.match(/^52\d{3}$/) && fn !== 'totalEquipmentHours') raw.equipment += val;
                else if (ct.match(/^53\d{3}$/)) raw.materials += val;
                else if (ct.match(/^54\d{3}$/)) raw.subs += val;
                else if (ct.match(/^5[5-7]\d{3}$/)) raw.other += val;
            });

            // Compare against displayed totals ($2 tolerance)
            const mismatches = [];
            const close = (a, b) => Math.abs(pNum(a) - pNum(b)) <= 2;
            const fmt = (v) => pNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            if (!close(totals.totalRevenue, raw.revenue)) mismatches.push(`Revenue: ${fmt(totals.totalRevenue)} vs ${fmt(raw.revenue)} (upload)`);
            if (!close(totals.labor, raw.labor)) mismatches.push(`Labor $: ${fmt(totals.labor)} vs ${fmt(raw.labor)} (upload)`);
            if (!close(totals.equipment, raw.equipment)) mismatches.push(`Equipment $: ${fmt(totals.equipment)} vs ${fmt(raw.equipment)} (upload)`);
            if (!close(totals.materialsTotal, raw.materials)) mismatches.push(`Materials $: ${fmt(totals.materialsTotal)} vs ${fmt(raw.materials)} (upload)`);
            if (!close(totals.subs, raw.subs)) mismatches.push(`Subs $: ${fmt(totals.subs)} vs ${fmt(raw.subs)} (upload)`);
            if (!close(totals.otherExpenses, raw.other)) mismatches.push(`Other $: ${fmt(totals.otherExpenses)} vs ${fmt(raw.other)} (upload)`);
            if (!close(totals.totalLaborHours, raw.laborHrs)) mismatches.push(`Labor Hrs: ${fmt(totals.totalLaborHours)} vs ${fmt(raw.laborHrs)} (upload)`);
            if (!close(totals.totalEquipmentHours, raw.equipHrs)) mismatches.push(`Equip Hrs: ${fmt(totals.totalEquipmentHours)} vs ${fmt(raw.equipHrs)} (upload)`);
            if (!close(totals.budgetQty, raw.budQty)) mismatches.push(`Budget Qty: ${fmt(totals.budgetQty)} vs ${fmt(raw.budQty)} (upload)`);

            setValidationChips(mismatches);
        } catch (e) {
            console.error('Validation chip error:', e);
            setValidationChips([]);
        }
    }, [totals, jobUserData, selectedJob, selectedVersion]);
    const saveRow = async (row) => {
        if (!row.coNo) { alert('CO Number is required'); return; }
        setIsSaving(true);
        try {
            const rowsToSave = [];
            const coNumber = row.coNo.toString();

            const typeMap = {
                "Internal Change": "Internal C",
                "Customer Requested": "Customer R",
                "Unit Adder": "Unit Adder",
                "Bid Miss": "Bid Miss",
            };

            const revenueFieldMap = {
                'Unit Price': 'unitPrice',
                'T&M': 'tAndM',
                'Change Order': 'changeOrders',
                'Reduction of Scope': 'reductionOfScope',
            };

            // 1. Header row
            const headerRowId = `${coNumber}_header_${generateId()}`;
            rowsToSave.push({
                userId: userId || '154045',
                userName: userName || 'Seth Bailey',
                tab: "Change Order Header",
                rowId: headerRowId,
                version: selectedVersion + '',
                id: selectedJob,
                lockedVersion: lockedVersion,
                fieldName: 'Row_Label',
                value: coNumber,
                valueAmount: isNaN(pNum(row.extension)) ? 0 : pNum(row.extension),
                costCode: row.phase || '',
                rowLabel: coNumber,
                description: row.description || '',
                status: row.status || '',
                notes: row.notes || '',
                po: typeMap[row.type] || row.type || '',
                quantity: '',
                resourceCode: row.revenueType || '',
                active: row.task || false,
                projectStart: row.initiated || '',
                projectEnd: row.submitted || '',
                phaseStart: row.signed || '',
                equipmentTier: row.criticalPath || false,
            });

            // 2. Revenue rows
            const revenueFields = ['unitPrice', 'tAndM', 'changeOrders', 'reductionOfScope'];
            revenueFields.forEach(fn => {
                if (pNum(row[fn]) !== 0) {
                    const uid = `${coNumber}_${fn}_${generateId()}`;
                    rowsToSave.push({
                        userId: userId || '154045',
                        userName: userName || 'Seth Bailey',
                        tab: "Change Order",
                        rowId: uid,
                        version: selectedVersion + '',
                        id: selectedJob,
                        lockedVersion: lockedVersion,
                        fieldName: fn,
                        value: row[fn] + '',
                        valueAmount: pNum(row[fn]),
                        costCode: row.phase || '',
                        costType: '41000',
                        rowLabel: coNumber,
                        status: row.status || '',
                        resourceCode: '',
                        hours: 0,
                        quantity: 0,
                        description: row.description || '',
                        notes: row.notes || '',
                    });
                }
            });

            // 3. Units (budgetQty, labor hours, equipment hours)
            if (pNum(row.budgetQty) !== 0) {
                const uid = `${coNumber}_budgetQty_${generateId()}`;
                rowsToSave.push({
                    userId: userId || '154045',
                    userName: userName || 'Seth Bailey',
                    tab: "Change Order",
                    rowId: uid,
                    version: selectedVersion + '',
                    id: selectedJob,
                    lockedVersion: lockedVersion,
                    fieldName: 'budgetQty',
                    value: row.budgetQty + '',
                    valueAmount: 0,
                    costCode: row.phase || '',
                    costType: '',
                    rowLabel: coNumber,
                    status: row.status || '',
                    resourceCode: '',
                    hours: 0,
                    quantity: pNum(row.budgetQty),
                    description: row.description || '',
                    notes: row.notes || '',
                });
            }
            if (pNum(row.totalLaborHours) !== 0) {
                const uid = `${coNumber}_totalLaborHours_${generateId()}`;
                rowsToSave.push({
                    userId: userId || '154045',
                    userName: userName || 'Seth Bailey',
                    tab: "Change Order",
                    rowId: uid,
                    version: selectedVersion + '',
                    id: selectedJob,
                    lockedVersion: lockedVersion,
                    fieldName: 'totalLaborHours',
                    value: row.totalLaborHours + '',
                    valueAmount: 0,
                    costCode: row.phase || '',
                    costType: '51100',
                    rowLabel: coNumber,
                    status: row.status || '',
                    resourceCode: '',
                    hours: pNum(row.totalLaborHours),
                    quantity: 0,
                    description: row.description || '',
                    notes: row.notes || '',
                });
            }
            if (pNum(row.totalEquipmentHours) !== 0) {
                const uid = `${coNumber}_totalEquipmentHours_${generateId()}`;
                rowsToSave.push({
                    userId: userId || '154045',
                    userName: userName || 'Seth Bailey',
                    tab: "Change Order",
                    rowId: uid,
                    version: selectedVersion + '',
                    id: selectedJob,
                    lockedVersion: lockedVersion,
                    fieldName: 'totalEquipmentHours',
                    value: row.totalEquipmentHours + '',
                    valueAmount: 0,
                    costCode: row.phase || '',
                    costType: '52100',
                    rowLabel: coNumber,
                    status: row.status || '',
                    resourceCode: '',
                    hours: pNum(row.totalEquipmentHours),
                    quantity: 0,
                    description: row.description || '',
                    notes: row.notes || '',
                });
            }

            // 4. Cost code rows (5xxxx)
            const costCodes = [
                '51100', '51200', '51300', '51400', '51500', '51600', '51700', '51800',
                '52100', '52200', '52300', '52400',
                '53100', '53200', '53300', '53400',
                '54100', '54200', '54300',
                '55100', '55200', '55300', '55400', '55900',
                '56100', '56200', '56300', '56400', '56500', '56600', '56700', '56800',
                '57100', '57200', '57300', '57900',
            ];
            costCodes.forEach(cc => {
                if (pNum(row[cc]) !== 0) {
                    const uid = `${coNumber}_${cc}_${generateId()}`;
                    rowsToSave.push({
                        userId: userId || '154045',
                        userName: userName || 'Seth Bailey',
                        tab: "Change Order",
                        rowId: uid,
                        version: selectedVersion + '',
                        id: selectedJob,
                        lockedVersion: lockedVersion,
                        fieldName: cc,
                        value: row[cc] + '',
                        valueAmount: pNum(row[cc]),
                        costCode: row.phase || '',
                        costType: cc,
                        rowLabel: coNumber,
                        status: row.status || '',
                        resourceCode: '',
                        hours: 0,
                        quantity: 0,
                        description: row.description || '',
                        notes: row.notes || '',
                    });
                }
            });

            // Delete existing rows for this CO
            if (!row._isNew) {
                try {
                    await deleteCO({ id: selectedJob, forecastGuid: selectedVersion.toString(), rowLabel: row.coNo });
                } catch (err) {
                    console.warn('Could not delete old CO rows:', err);
                }
            }

            await updateUserDataArray(rowsToSave);

            setCOLogs(prev => prev.map(r =>
                r._localId === row._localId ? { ...r, _dirty: false, _isNew: false } : r
            ));
            console.log(`Saved CO #${coNumber}: ${rowsToSave.length} rows`);
        } catch (error) {
            console.error('Error saving CO entry:', error);
            alert('Error saving change order. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const addBlankRow = () => {
        setCOLogs(prev => [...prev, {
            _localId: generateId(),
            coNo: '', phase: '', description: '', status: 'Not Submitted',
            type: '', revenueType: '', notes: '',
            initiated: '', submitted: '', signed: '', extension: '',
            task: false, criticalPath: false,
            unitPrice: 0, tAndM: 0, changeOrders: 0, reductionOfScope: 0,
            budgetQty: 0, totalLaborHours: 0, totalEquipmentHours: 0,
            '51100': 0, '51200': 0, '51300': 0, '51400': 0, '51500': 0, '51600': 0, '51700': 0, '51800': 0,
            '52100': 0, '52200': 0, '52300': 0, '52400': 0,
            '53100': 0, '53200': 0, '53300': 0, '53400': 0,
            '54100': 0, '54200': 0, '54300': 0,
            '55100': 0, '55200': 0, '55300': 0, '55400': 0, '55900': 0,
            '56100': 0, '56200': 0, '56300': 0, '56400': 0, '56500': 0, '56600': 0, '56700': 0, '56800': 0,
            '57100': 0, '57200': 0, '57300': 0, '57900': 0,
            _isNew: true, _dirty: true,
        }]);
    };

    const deleteRow = async (localId) => {
        const row = coLogs.find(r => r._localId === localId);
        if (!row) return;
        if (!row._isNew && row.coNo) {
            if (!confirm(`Delete CO #${row.coNo}? This will remove all associated data rows.`)) return;
            try {
                await deleteCO({ id: selectedJob, forecastGuid: selectedVersion.toString(), rowLabel: row.coNo });
            } catch (err) {
                console.error('Error deleting CO:', err);
            }
        }
        setCOLogs(prev => prev.filter(r => r._localId !== localId));
    };

    const updateField = (localId, key, value) => {
        setCOLogs(prev => {
            const updated = prev.map(r =>
                r._localId === localId ? { ...r, [key]: value, _dirty: true } : r
            );
            // Auto-save with debounce
            if (autoSaveTimers.current[localId]) clearTimeout(autoSaveTimers.current[localId]);
            autoSaveTimers.current[localId] = setTimeout(() => {
                const row = updated.find(r => r._localId === localId);
                if (row && row._dirty && row.coNo) saveRow(row);
            }, 1500);
            return updated;
        });
    };

    // -----------------------------------------------------------
    // CORow component – mirrors colog.js layout exactly
    // -----------------------------------------------------------
    const CORow = ({ localId, highlighted }) => {
        const row = coLogs.find(r => r._localId === localId);
        if (!row) return null;

        const calc = calcRow(row);
        const inputsRef = useRef({});

        // Debounce per row
        const changeListRef = useRef([]);
        const debounceTimer = useRef(null);

        const handleChange = (key, rawValue) => {
            const value = (typeof rawValue === 'object' && rawValue !== null && 'floatValue' in rawValue) ? rawValue.floatValue : rawValue;
            updateField(localId, key, value);
        };

        const editBar = () => (
            <View style={{ width: 0, height: '70%', borderLeft: '3px solid #808080', left: 5, top: '15%', position: 'absolute', zIndex: 2 }} />
        );

        const numCell = (key, width) => (
            <View style={{ position: 'relative' }}>
                {editBar()}
                <NumberInput
                    style={highlighted ? s(width).span1T : s(width).spanT}
                    thousandSeparator={true}
                    decimalSeparator="."
                    valueIsNumericString={true}
                    value={row[key] || ''}
                    onValueChange={(vals) => handleChange(key, vals.floatValue || 0)}
                />
            </View>
        );

        const readonlyCell = (value, width) => (
            <Text style={[s(width).spanT, { backgroundColor: '#f8fafc' }]}>
                {fNum(value)}
            </Text>
        );

        return (
            <View style={s().container}>
                {/* CO No. */}
                <View style={{ position: 'relative' }}>
                    {editBar()}
                    <TextInput
                        style={highlighted ? s(80).span1T : s(80).spanT}
                        value={row.coNo}
                        onChangeText={(t) => handleChange('coNo', t)}
                    />
                </View>
                {/* Phase */}
                <View style={{ position: 'relative' }}>
                    {editBar()}
                    <TextInput
                        style={highlighted ? s(80).span1T : s(80).spanT}
                        value={row.phase}
                        onChangeText={(t) => handleChange('phase', t)}
                    />
                </View>
                {/* Item Description */}
                <View style={{ position: 'relative' }}>
                    {editBar()}
                    <TextInput
                        style={highlighted ? s(300).span1T : s(300).spanT}
                        value={row.description}
                        onChangeText={(t) => handleChange('description', t.slice(0, MAX_DESCRIPTION_LENGTH))}
                        maxLength={MAX_DESCRIPTION_LENGTH}
                    />
                </View>
                {/* Status */}
                <View style={{ position: 'relative' }}>
                    {editBar()}
                    <select
                        style={{
                            width: 200, height: 38, fontSize: 13, padding: '8px 6px',
                            borderBottom: '1px solid #e2e8f0', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                            backgroundColor: 'transparent', outline: 'none', cursor: 'pointer',
                        }}
                        value={row.status}
                        onChange={(e) => handleChange('status', e.target.value)}
                    >
                        <option value="">--</option>
                        {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </View>

                {/* PSR Details (expandable) */}
                {psrExpanded ? <View style={[s().container, { backgroundColor: '#f0f7ff' }]}>
                    {/* Type */}
                    <View style={{ position: 'relative' }}>
                        {editBar()}
                        <select
                            style={{
                                width: 140, height: 38, fontSize: 13, padding: '8px 6px',
                                borderBottom: '1px solid #e2e8f0', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                                backgroundColor: 'transparent', outline: 'none', cursor: 'pointer',
                            }}
                            value={row.type}
                            onChange={(e) => handleChange('type', e.target.value)}
                        >
                            <option value="">--</option>
                            <option value="Internal Change">Internal Change</option>
                            <option value="Customer Requested">Customer Requested</option>
                            <option value="Unit Adder">Unit Adder</option>
                            <option value="Bid Miss">Bid Miss</option>
                        </select>
                    </View>
                    {/* Revenue Type */}
                    <View style={{ position: 'relative' }}>
                        {editBar()}
                        <select
                            style={{
                                width: 135, height: 38, fontSize: 13, padding: '8px 6px',
                                borderBottom: '1px solid #e2e8f0', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                                backgroundColor: 'transparent', outline: 'none', cursor: 'pointer',
                            }}
                            value={row.revenueType}
                            onChange={(e) => handleChange('revenueType', e.target.value)}
                        >
                            <option value="">--</option>
                            <option value="Unit Price">Unit Price</option>
                            <option value="T&M">T&M</option>
                            <option value="Change Order">Change Order</option>
                            <option value="Reduction of Scope">Reduction of Scope</option>
                        </select>
                    </View>
                    {/* Initiated */}
                    <View style={{ position: 'relative' }}>
                        {editBar()}
                        <input
                            type="date"
                            style={{
                                width: 120, height: 38, fontSize: 13, padding: '8px 6px',
                                borderBottom: '1px solid #e2e8f0', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                                backgroundColor: 'transparent', outline: 'none',
                            }}
                            value={row.initiated ? row.initiated.substring(0, 10) : ''}
                            onChange={(e) => handleChange('initiated', e.target.value)}
                        />
                    </View>
                    {/* Submitted */}
                    <View style={{ position: 'relative' }}>
                        {editBar()}
                        <input
                            type="date"
                            style={{
                                width: 120, height: 38, fontSize: 13, padding: '8px 6px',
                                borderBottom: '1px solid #e2e8f0', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                                backgroundColor: 'transparent', outline: 'none',
                            }}
                            value={row.submitted ? row.submitted.substring(0, 10) : ''}
                            onChange={(e) => handleChange('submitted', e.target.value)}
                        />
                    </View>
                    {/* Signed */}
                    <View style={{ position: 'relative' }}>
                        {editBar()}
                        <input
                            type="date"
                            style={{
                                width: 120, height: 38, fontSize: 13, padding: '8px 6px',
                                borderBottom: '1px solid #e2e8f0', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                                backgroundColor: 'transparent', outline: 'none',
                            }}
                            value={row.signed ? row.signed.substring(0, 10) : ''}
                            onChange={(e) => handleChange('signed', e.target.value)}
                        />
                    </View>
                    {/* Extension */}
                    <View style={{ position: 'relative' }}>
                        {editBar()}
                        <TextInput
                            style={highlighted ? s(80).span1T : s(80).spanT}
                            value={row.extension?.toString() || ''}
                            onChangeText={(t) => handleChange('extension', t)}
                        />
                    </View>
                    {/* Task */}
                    <Pressable
                        style={{ width: 70, height: 38, justifyContent: 'center', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}
                        onPress={() => handleChange('task', !row.task)}
                    >
                        <Text style={{ fontSize: 16 }}>{row.task ? '☑' : '☐'}</Text>
                    </Pressable>
                    {/* Critical Path */}
                    <Pressable
                        style={{ width: 70, height: 38, justifyContent: 'center', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}
                        onPress={() => handleChange('criticalPath', !row.criticalPath)}
                    >
                        <Text style={{ fontSize: 16 }}>{row.criticalPath ? '☑' : '☐'}</Text>
                    </Pressable>
                </View> : null}

                {/* PSR Details spacer cell */}
                <Text style={s().spanT}></Text>

                <View style={{ width: 20 }} />

                {/* Revenue columns */}
                {numCell('unitPrice')}
                {numCell('tAndM')}
                {numCell('changeOrders')}
                {numCell('reductionOfScope')}
                {readonlyCell(calc.totalRevenue)}

                <View style={{ width: 20 }} />

                {/* Expandable cost columns */}
                {totalExpanded ? <View style={s().container}>
                    {/* Units section */}
                    {readonlyCell(pNum(row.budgetQty))}
                    {unitsExpanded ? <View style={s().container}>
                        {numCell('budgetQty')}
                        {numCell('totalLaborHours')}
                        {numCell('totalEquipmentHours')}
                    </View> : null}

                    {/* Labor */}
                    {readonlyCell(calc.labor)}
                    {laborExpanded ? <View style={s().container}>
                        {numCell('51100')}
                        {numCell('51200')}
                        {numCell('51300')}
                        {numCell('51400')}
                        {numCell('51500')}
                        {numCell('51600')}
                        {numCell('51700')}
                        {numCell('51800')}
                    </View> : null}

                    {/* Equipment */}
                    {readonlyCell(calc.equipment)}
                    {equipmentExpanded ? <View style={s().container}>
                        {numCell('52100')}
                        {numCell('52200')}
                        {numCell('52300')}
                        {numCell('52400')}
                    </View> : null}

                    {/* Materials */}
                    {readonlyCell(calc.materialsTotal)}
                    {materialsExpanded ? <View style={s().container}>
                        {numCell('53100')}
                        {numCell('53200')}
                        {numCell('53300')}
                        {numCell('53400')}
                    </View> : null}

                    {/* Subs */}
                    {readonlyCell(calc.subs)}
                    {subsExpanded ? <View style={s().container}>
                        {numCell('54100')}
                        {numCell('54200')}
                        {numCell('54300')}
                    </View> : null}

                    {/* Other Expenses */}
                    {readonlyCell(calc.otherExpenses)}
                    {otherExpanded ? <View style={s().container}>
                        {numCell('55100')}
                        {numCell('55200')}
                        {numCell('55300')}
                        {numCell('55400')}
                        {numCell('55900')}
                        {numCell('56100')}
                        {numCell('56200')}
                        {numCell('56300')}
                        {numCell('56400')}
                        {numCell('56500')}
                        {numCell('56600')}
                        {numCell('56700')}
                        {numCell('56800')}
                        {numCell('57100')}
                        {numCell('57200')}
                        {numCell('57300')}
                        {numCell('57900')}
                    </View> : null}

                    {/* Overhead */}
                    {readonlyCell(calc.overhead)}
                </View> : null}

                {/* Total Costs */}
                {readonlyCell(calc.totalCosts)}
                {/* Gross Margin */}
                {readonlyCell(calc.grossProfit)}
                {/* GM % */}
                <Text style={[s().spanT, { backgroundColor: '#f8fafc' }]}>
                    {calc.gpPercent !== 0 ? calc.gpPercent.toFixed(1) + '%' : ''}
                </Text>

                {/* Additional Notes */}
                <View style={{ position: 'relative' }}>
                    {editBar()}
                    <TextInput
                        style={highlighted ? s(350).span1T : s(350).spanT}
                        value={row.notes}
                        onChangeText={(t) => handleChange('notes', t)}
                        multiline={true}
                    />
                </View>

                {/* Delete */}
                <Pressable
                    style={{ padding: 8, justifyContent: 'center' }}
                    onPress={() => deleteRow(localId)}
                >
                    <Icon3 name="MdDelete" size={18} color="red" />
                </Pressable>
                {row._dirty ? <Text style={{ fontSize: 10, color: '#94a3b8', alignSelf: 'center', marginLeft: 2 }}>saving...</Text> : null}
            </View>
        );
    };

    // -----------------------------------------------------------
    // Header row – identical to colog.js
    // -----------------------------------------------------------
    const Header = () => (
        <View style={[s().container, { position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }]}>
            <Text style={s(80).spanH}>CO NO.</Text>
            <Text style={s(80).spanH}>Phase</Text>
            <Text style={s(300).spanH}>Item Description</Text>
            <Text style={s(200).spanH}>Status</Text>
            {psrExpanded ? <View style={[s().container, { backgroundColor: '#e8f0fe' }]}>
                <Text style={[s(140).spanH, { backgroundColor: '#e8f0fe' }]}>Type</Text>
                <Text style={[s(135).spanH, { backgroundColor: '#e8f0fe' }]}>Revenue Type</Text>
                <Text style={[s(120).spanH, { backgroundColor: '#e8f0fe' }]}>Initiated</Text>
                <Text style={[s(120).spanH, { backgroundColor: '#e8f0fe' }]}>Submitted</Text>
                <Text style={[s(120).spanH, { backgroundColor: '#e8f0fe' }]}>Signed</Text>
                <Text style={[s(80).spanH, { backgroundColor: '#e8f0fe' }]}>Extension</Text>
                <Text style={[s(70).spanH, { backgroundColor: '#e8f0fe' }]}>Task</Text>
                <Text style={[s(70).spanH, { backgroundColor: '#e8f0fe' }]}>Critical</Text>
            </View> : null}
            <Text style={s().spanH}>
                PSR Details
                <Icon3 name={psrExpanded ? "MdExpandMore" : "MdChevronRight"} size={20} color="black"
                    onClick={() => setPsrExpanded(!psrExpanded)} />
            </Text>
            <View style={{ width: 20 }} />
            <Text style={s().spanH}>Unit Price Adders</Text>
            <Text style={s().spanH}>T&M</Text>
            <Text style={s().spanH}>Change Orders</Text>
            <Text style={s().spanH}>Reduction of Scope</Text>
            <Text style={s().spanH}>Total Revenue</Text>
            <View style={{ width: 20 }} />
            {totalExpanded ? <View style={s().container}>
                <Text style={s().spanHI}>Units
                    <Icon3 name={unitsExpanded ? "MdExpandMore" : "MdChevronRight"} size={20} color="black"
                        onClick={() => setUnitsExpanded(!unitsExpanded)} />
                </Text>
                {unitsExpanded ? <View style={s().container}>
                    <Text style={s().spanH}>Budgeted Qty</Text>
                    <Text style={s().spanH}>Labor Hours</Text>
                    <Text style={s().spanH}>Equipment Hours</Text>
                </View> : null}
                <Text style={s().spanHI}>Labor
                    <Icon3 name={laborExpanded ? "MdExpandMore" : "MdChevronRight"} size={20} color="black"
                        onClick={() => setLaborExpanded(!laborExpanded)} />
                </Text>
                {laborExpanded ? <View style={s().container}>
                    <Text style={s().spanH}>51100 Regular Time</Text>
                    <Text style={s().spanH}>51200 Overtime</Text>
                    <Text style={s().spanH}>51300 Burden</Text>
                    <Text style={s().spanH}>51400 BYOD</Text>
                    <Text style={s().spanH}>51500 Per Diem</Text>
                    <Text style={s().spanH}>51600 Truck Pay</Text>
                    <Text style={s().spanH}>51700 Welding Rig Pay</Text>
                    <Text style={s().spanH}>51800 Field Bonus</Text>
                </View> : null}
                <Text style={s().spanHI}><Text>Equipment</Text>
                    <Icon3 name={equipmentExpanded ? "MdExpandMore" : "MdChevronRight"} size={20} color="black"
                        onClick={() => setEquipmentExpanded(!equipmentExpanded)} />
                </Text>
                {equipmentExpanded ? <View style={s().container}>
                    <Text style={s().spanH}>52100 Inside Equipment Rental</Text>
                    <Text style={s().spanH}>52200 Outside Equipment Rental</Text>
                    <Text style={s().spanH}>52300 Fuel</Text>
                    <Text style={s().spanH}>52400 Repairs</Text>
                </View> : null}
                <Text style={s().spanHI}><Text>Materials</Text>
                    <Icon3 name={materialsExpanded ? "MdExpandMore" : "MdChevronRight"} size={20} color="black"
                        onClick={() => setMaterialsExpanded(!materialsExpanded)} />
                </Text>
                {materialsExpanded ? <View style={s().container}>
                    <Text style={s().spanH}>53100 Materials</Text>
                    <Text style={s().spanH}>53200 Mats</Text>
                    <Text style={s().spanH}>53300 Tools/Supplies</Text>
                    <Text style={s().spanH}>53400 Skids</Text>
                </View> : null}
                <Text style={s().spanHI}>Subs
                    <Icon3 name={subsExpanded ? "MdExpandMore" : "MdChevronRight"} size={20} color="black"
                        onClick={() => setSubsExpanded(!subsExpanded)} />
                </Text>
                {subsExpanded ? <View style={s().container}>
                    <Text style={s().spanH}>54100 Internal Subs</Text>
                    <Text style={s().spanH}>54200 External Subs</Text>
                    <Text style={s().spanH}>54300 Hauling/Transportation</Text>
                </View> : null}
                <Text style={s().spanHI}>Other Expenses
                    <Icon3 name={otherExpanded ? "MdExpandMore" : "MdChevronRight"} size={20} color="black"
                        onClick={() => setOtherExpanded(!otherExpanded)} />
                </Text>
                {otherExpanded ? <View style={s().container}>
                    <Text style={s().spanH}>55100 Personal Meals</Text>
                    <Text style={s().spanH}>55200 Team Meals/Entertainment</Text>
                    <Text style={s().spanH}>55300 Airfare</Text>
                    <Text style={s().spanH}>55400 Lodging</Text>
                    <Text style={s().spanH}>55900 Other Travel Expenses</Text>
                    <Text style={s().spanH}>56100 Field Office Rent</Text>
                    <Text style={s().spanH}>56200 Field Office Utility</Text>
                    <Text style={s().spanH}>56300 Field Office Expense</Text>
                    <Text style={s().spanH}>56400 Lab Fees/Testing</Text>
                    <Text style={s().spanH}>56500 Safety/PPE</Text>
                    <Text style={s().spanH}>56600 Bonds/Insurance Premiums</Text>
                    <Text style={s().spanH}>56700 Permits</Text>
                    <Text style={s().spanH}>56800 Insurance Claims</Text>
                    <Text style={s().spanH}>57100 Taxes</Text>
                    <Text style={s().spanH}>57200 Accrued Expenses</Text>
                    <Text style={s().spanH}>57300 Warranty Reserve</Text>
                    <Text style={s().spanH}>57900 Misc</Text>
                </View> : null}
                <Text style={s().spanHI}>Overhead Allocation</Text>
            </View> : null}
            <Text style={s().spanH}>
                Total Costs
                <Icon3 name={totalExpanded ? "MdExpandMore" : "MdChevronRight"} size={20} color="black"
                    onClick={() => setTotalExpanded(!totalExpanded)} />
            </Text>
            <Text style={s().spanH}>Gross Margin</Text>
            <Text style={s().spanH}>GM %</Text>
            <Text style={s(350).spanH}>Additional Notes</Text>
        </View>
    );

    // -----------------------------------------------------------
    // Footer row
    // -----------------------------------------------------------
    const Footer = () => {
        const tv = (key) => fNum(totals[key] || 0);
        const av = (key) => fNum(totalsApproved[key] || 0);
        const pv = (key) => fNum(totalsPending[key] || 0);

        const FooterRow = ({ label, val, gpPct, leftWidth }) => (
            <View style={s().container}>
                <View style={{ width: leftWidth || 460 }} />
                <Text style={s(leftWidth ? 300 : 200).spanH}>{label}</Text>
                {!leftWidth && psrExpanded ? <View style={[s().container, { backgroundColor: '#e8f0fe' }]}>
                    <Text style={[s(140).spanH, { backgroundColor: '#e8f0fe' }]}></Text>
                    <Text style={[s(135).spanH, { backgroundColor: '#e8f0fe' }]}></Text>
                    <Text style={[s(120).spanH, { backgroundColor: '#e8f0fe' }]}></Text>
                    <Text style={[s(120).spanH, { backgroundColor: '#e8f0fe' }]}></Text>
                    <Text style={[s(120).spanH, { backgroundColor: '#e8f0fe' }]}></Text>
                    <Text style={[s(80).spanH, { backgroundColor: '#e8f0fe' }]}></Text>
                    <Text style={[s(70).spanH, { backgroundColor: '#e8f0fe' }]}></Text>
                    <Text style={[s(70).spanH, { backgroundColor: '#e8f0fe' }]}></Text>
                </View> : null}
                {!leftWidth && <Text style={s().spanH}></Text>}
                <View style={{ width: 20 }} />
                <Text style={s().spanH}>{val('unitPrice')}</Text>
                <Text style={s().spanH}>{val('tAndM')}</Text>
                <Text style={s().spanH}>{val('changeOrders')}</Text>
                <Text style={s().spanH}>{val('reductionOfScope')}</Text>
                <Text style={s().spanH}>{val('totalRevenue')}</Text>
                <View style={{ width: 20 }} />
                {totalExpanded ? <View style={s().container}>
                    <Text style={s().spanH}>{val('budgetQty')}</Text>
                    {unitsExpanded ? <View style={s().container}>
                        <Text style={s().spanH}></Text>
                        <Text style={s().spanH}>{val('totalLaborHours')}</Text>
                        <Text style={s().spanH}>{val('totalEquipmentHours')}</Text>
                    </View> : null}
                    <Text style={s().spanH}>{val('labor')}</Text>
                    {laborExpanded ? <View style={s().container}>
                        <Text style={s().spanH}>{val('51100')}</Text>
                        <Text style={s().spanH}>{val('51200')}</Text>
                        <Text style={s().spanH}>{val('51300')}</Text>
                        <Text style={s().spanH}>{val('51400')}</Text>
                        <Text style={s().spanH}>{val('51500')}</Text>
                        <Text style={s().spanH}>{val('51600')}</Text>
                        <Text style={s().spanH}>{val('51700')}</Text>
                        <Text style={s().spanH}>{val('51800')}</Text>
                    </View> : null}
                    <Text style={s().spanH}>{val('equipment')}</Text>
                    {equipmentExpanded ? <View style={s().container}>
                        <Text style={s().spanH}>{val('52100')}</Text>
                        <Text style={s().spanH}>{val('52200')}</Text>
                        <Text style={s().spanH}>{val('52300')}</Text>
                        <Text style={s().spanH}>{val('52400')}</Text>
                    </View> : null}
                    <Text style={s().spanH}>{val('materialsTotal')}</Text>
                    {materialsExpanded ? <View style={s().container}>
                        <Text style={s().spanH}>{val('53100')}</Text>
                        <Text style={s().spanH}>{val('53200')}</Text>
                        <Text style={s().spanH}>{val('53300')}</Text>
                        <Text style={s().spanH}>{val('53400')}</Text>
                    </View> : null}
                    <Text style={s().spanH}>{val('subs')}</Text>
                    {subsExpanded ? <View style={s().container}>
                        <Text style={s().spanH}>{val('54100')}</Text>
                        <Text style={s().spanH}>{val('54200')}</Text>
                        <Text style={s().spanH}>{val('54300')}</Text>
                    </View> : null}
                    <Text style={s().spanH}>{val('otherExpenses')}</Text>
                    {otherExpanded ? <View style={s().container}>
                        <Text style={s().spanH}>{val('55100')}</Text>
                        <Text style={s().spanH}>{val('55200')}</Text>
                        <Text style={s().spanH}>{val('55300')}</Text>
                        <Text style={s().spanH}>{val('55400')}</Text>
                        <Text style={s().spanH}>{val('55900')}</Text>
                        <Text style={s().spanH}>{val('56100')}</Text>
                        <Text style={s().spanH}>{val('56200')}</Text>
                        <Text style={s().spanH}>{val('56300')}</Text>
                        <Text style={s().spanH}>{val('56400')}</Text>
                        <Text style={s().spanH}>{val('56500')}</Text>
                        <Text style={s().spanH}>{val('56600')}</Text>
                        <Text style={s().spanH}>{val('56700')}</Text>
                        <Text style={s().spanH}>{val('56800')}</Text>
                        <Text style={s().spanH}>{val('57100')}</Text>
                        <Text style={s().spanH}>{val('57200')}</Text>
                        <Text style={s().spanH}>{val('57300')}</Text>
                        <Text style={s().spanH}>{val('57900')}</Text>
                    </View> : null}
                    <Text style={s().spanH}>{val('overhead')}</Text>
                </View> : null}
                <Text style={s().spanH}>{val('totalCosts')}</Text>
                <Text style={s().spanH}>{val('grossProfit')}</Text>
                <Text style={s().spanH}>{gpPct ? gpPct.toFixed(1) + '%' : ''}</Text>
                <Text style={s(350).spanH}></Text>
            </View>
        );

        return (
            <View>
                <FooterRow label="Change Order Total" val={tv} gpPct={totals.gpPercent} />
                <FooterRow label="Approved Total" val={av} gpPct={totalsApproved.gpPercent} leftWidth={360} />
                <FooterRow label="Pending Total" val={pv} gpPct={totalsPending.gpPercent} leftWidth={360} />
            </View>
        );
    };

    // -----------------------------------------------------------
    // Main render
    // -----------------------------------------------------------
    return (
        <View>
            {/* Table */}
            <View style={{ backgroundColor: 'white', borderRadius: 5, margin: 30, marginBottom: 100, position: 'relative',transform: 'scale(0.7)', transformOrigin: 'top left' }}>
                <View style={{ flexDirection: 'column', maxWidth: 'unset' }}>
                    <Header />
                    {coLogs.map((co) => (
                        <CORow key={co._localId} localId={co._localId} />
                    ))}
                    {/* Add Change Order row */}
                    <Pressable
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            paddingVertical: 6,
                            paddingHorizontal: 8,
                            cursor: 'pointer',
                        }}
                        onPress={addBlankRow}
                    >
                        <Icon3 name="MdAdd" size={16} color="#2563eb" />
                        <Text style={{ color: '#2563eb', fontWeight: '600', fontSize: 13, marginLeft: 4 }}>Add Change Order</Text>
                    </Pressable>
                    <Footer />
                </View>
            </View>
        </View>
    );
};

export default COLogEntryClassic;
